import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Download, Edit2, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from '../../api/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';

const AMBITO_STYLE = { nacional: 'bg-red-100 text-red-800', regional: 'bg-blue-100 text-blue-800', local: 'bg-emerald-100 text-emerald-800' };

const formatFecha = (iso) => {
  const [y, m, d] = iso.split('-');
  const dia = new Date(+y, +m - 1, +d).toLocaleDateString('es-ES', { weekday: 'long' });
  return `${d}/${m}/${y} · ${dia}`;
};

const FestivosTab = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [festivos, setFestivos] = useState([]);
  const [nuevo, setNuevo] = useState({ fecha: '', nombre: '' });
  const [importing, setImporting] = useState(false);
  const [editing, setEditing] = useState(null);

  const saveEdit = async () => {
    if (!editing.fecha || !editing.nombre.trim()) return toast.error('Indica fecha y nombre');
    try {
      await axios.put(`/festivos/${editing.id}`, { fecha: editing.fecha, nombre: editing.nombre, ambito: editing.ambito });
      toast.success('Festivo actualizado');
      setEditing(null);
      fetchFestivos();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar el festivo');
    }
  };

  const fetchFestivos = () => axios.get('/festivos', { params: { year } }).then((r) => setFestivos(r.data));
  useEffect(() => { fetchFestivos(); }, [year]); // eslint-disable-line
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - 1 + i);

  const importar = async () => {
    setImporting(true);
    try {
      const r = await axios.post(`/festivos/importar/${year}`);
      toast.success(`${r.data.added} festivos importados (nacionales, C. Madrid y locales de Alcalá)`);
      fetchFestivos();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al importar festivos');
    } finally {
      setImporting(false);
    }
  };

  const add = async () => {
    if (!nuevo.fecha || !nuevo.nombre.trim()) return toast.error('Indica fecha y nombre');
    try {
      await axios.post('/festivos', { ...nuevo, ambito: 'local' });
      toast.success('Festivo añadido');
      setNuevo({ fecha: '', nombre: '' });
      fetchFestivos();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al añadir festivo');
    }
  };

  const remove = async (f) => {
    try {
      await axios.delete(`/festivos/${f.id}`);
      toast.success('Festivo eliminado');
      fetchFestivos();
    } catch {
      toast.error('Error al eliminar el festivo');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Festivos</CardTitle>
        <p className="text-sm text-gray-500">No se cuentan como días hábiles en F. Informe ni en las fechas de Salida Cámara.</p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap items-center gap-2">
          <select className="h-9 border rounded-md px-2 text-sm bg-white" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} data-testid="festivos-year-input">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <Button variant="outline" onClick={importar} disabled={importing} data-testid="import-festivos-btn">
            <Download size={16} className="mr-1" /> {importing ? 'Importando...' : `Importar festivos ${year}`}
          </Button>
        </div>
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
          La importación añade los festivos nacionales, de la Comunidad de Madrid y los locales de Alcalá de Henares (6 de agosto y 9 de octubre). No incluye los traslados (p.ej. de domingo a lunes): edita la fecha con el lápiz según el BOCM.
        </p>
        <div className="border p-4 rounded-lg bg-blue-50 flex flex-col md:flex-row gap-2">
          <Input type="date" className="md:w-48" value={nuevo.fecha} onChange={(e) => setNuevo({ ...nuevo, fecha: e.target.value })} data-testid="new-festivo-fecha" />
          <Input placeholder="Nombre (p.ej. Santos Justo y Pastor - Alcalá)" value={nuevo.nombre} onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })} data-testid="new-festivo-nombre" />
          <Button onClick={add} data-testid="add-festivo-btn"><Plus size={16} className="mr-1" /> Añadir local</Button>
        </div>
        <div className="space-y-2" data-testid="festivos-list">
          {festivos.length === 0 && <p className="text-sm text-gray-500">No hay festivos para {year}.</p>}
          {festivos.map((f) => (editing?.id === f.id ? (
            <div key={f.id} className="border p-3 rounded-lg flex flex-col md:flex-row md:items-center gap-2 bg-blue-50" data-testid={`festivo-edit-${f.id}`}>
              <Input type="date" className="md:w-48" value={editing.fecha} onChange={(e) => setEditing({ ...editing, fecha: e.target.value })} data-testid="edit-festivo-fecha" />
              <Input className="flex-1" value={editing.nombre} onChange={(e) => setEditing({ ...editing, nombre: e.target.value })} data-testid="edit-festivo-nombre" />
              <Button size="sm" onClick={saveEdit} data-testid="update-festivo-btn"><Save size={16} /></Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(null)} data-testid="cancel-edit-festivo-btn"><X size={16} /></Button>
            </div>
          ) : (
            <div key={f.id} className="border p-3 rounded-lg flex items-center gap-3" data-testid={`festivo-${f.fecha}`}>
              <span className="w-56 text-sm font-medium">{formatFecha(f.fecha)}</span>
              <span className="flex-1 text-sm">{f.nombre}</span>
              <Badge className={AMBITO_STYLE[f.ambito] || AMBITO_STYLE.local}>{f.ambito}</Badge>
              <Button size="sm" variant="outline" onClick={() => setEditing({ ...f })} data-testid={`edit-festivo-${f.fecha}`}><Edit2 size={16} /></Button>
              <Button size="sm" variant="destructive" onClick={() => remove(f)} data-testid={`delete-festivo-${f.fecha}`}><Trash2 size={16} /></Button>
            </div>
          )))}
        </div>
      </CardContent>
    </Card>
  );
};

export default FestivosTab;
